import { Badge } from '@systemeio/components'
import React, { type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

interface ChartCardProps {
  children: ReactNode
  className?: string
  headerAction?: ReactNode
  id?: string
  title: ReactNode
  value?: ReactNode
}

/*
 * The header keeps the title and its action on one line and pushes the absolute value into a boxed
 * slot on the opposite side. The title group stays `min-w-0` because overflow tooltips truncate
 * their own trigger, and the action and the value box never shrink. The chart wrapper stays
 * `relative` because `chart-config.ts` positions its DOM tooltip inside it.
 */
const ChartCard = ({ children, className, headerAction, id, title, value }: ChartCardProps) => (
  <div
    id={id}
    className={twMerge(
      'border-border-100 bg-bg-primary flex w-full flex-col justify-between gap-6 rounded-lg border p-6',
      className,
    )}
  >
    <div className={'flex items-center justify-between gap-3'}>
      <div className={'text-text-primary flex min-w-0 items-center gap-2 text-lg font-medium'}>
        {title}
        {headerAction !== undefined && <div className={'shrink-0'}>{headerAction}</div>}
      </div>
      {value !== undefined && (
        <Badge
          className={'shrink-0 text-base'}
          color={'outlinedGray'}
          render={<div />}
          shape={'rounded'}
          size={'lg'}
        >
          {value}
        </Badge>
      )}
    </div>
    <div className={'relative'}>{children}</div>
  </div>
)

export { ChartCard }
export type { ChartCardProps }
